<template>
    <section class="slg">
        <div class="slg-card">

            <!-- Header -->
            <div class="slg-head">
                <div class="slg-head-l">
                    <div class="slg-head-icon" :style="`background:rgba(${themeColors.primaryRgb},0.12);color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.22);`">
                        <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"/>
                        </svg>
                    </div>
                    <div>
                        <h2 class="slg-head-title">{{ t('stakingHistory') }}</h2>
                        <p class="slg-head-sub">{{ t('monitorStakingPositions') }}</p>
                    </div>
                </div>
                <div class="slg-total-chip" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.2);`">
                    <span class="slg-total-n" :style="`color:${themeColors.primary};`">{{ logs?.total || filteredLogs.length }}</span>
                    <span class="slg-total-l">{{ t('totalStakes') }}</span>
                </div>
            </div>

            <!-- Filter tabs -->
            <div class="slg-filters" role="tablist">
                <button
                    v-for="filter in filters"
                    :key="filter.value"
                    class="slg-filter-btn"
                    :class="activeFilter === filter.value ? 'slg-filter-btn--on' : ''"
                    :style="activeFilter === filter.value
                        ? `background:${themeColors.primary};color:#0f172a;box-shadow:0 4px 14px rgba(${themeColors.primaryRgb},0.35);`
                        : ''"
                    @click="activeFilter = filter.value"
                    role="tab"
                    :aria-selected="activeFilter === filter.value"
                >{{ filter.label }}</button>
            </div>

            <!-- Empty state -->
            <div v-if="!filteredLogs || filteredLogs.length === 0" class="slg-empty">
                <div class="slg-empty-ic" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.18);color:rgba(${themeColors.primaryRgb},0.6);`">
                    <svg width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"/>
                    </svg>
                </div>
                <p class="slg-empty-title">{{ t('noStakesFound') }}</p>
                <p class="slg-empty-desc">{{ t('startStakingToSeeActivities') }}</p>
            </div>

            <!-- ── MOBILE ROWS (< 1024px) ── -->
            <div v-else-if="!isDesktop" class="slg-mob">
                <div
                    v-for="log in filteredLogs"
                    :key="log.id"
                    class="slg-row"
                    @click="showStakeDetails(log)"
                    role="button"
                    tabindex="0"
                    @keydown.enter="showStakeDetails(log)"
                >
                    <!-- Left: dot + pool info -->
                    <div class="slg-row-l">
                        <span class="slg-row-dot" :style="`background:${getStatusBadgeColor(log.status)};box-shadow:0 0 8px ${getStatusBadgeColor(log.status)}60;`"></span>
                        <div class="slg-row-info">
                            <p class="slg-row-pool">
                                {{ log.pool_name }}
                                <span v-if="log.auto_compound" class="slg-auto-tag">AUTO</span>
                            </p>
                            <p class="slg-row-date">{{ formatDate(log.staked_at) }} · #{{ log.stake_id }}</p>
                            <p class="slg-row-btc">₿{{ formatBtc(getStakeBtc(log)) }}</p>
                        </div>
                    </div>

                    <!-- Right: rewards + status -->
                    <div class="slg-row-r">
                        <p class="slg-row-reward" :style="`color:${themeColors.primary};`">
                            +₿{{ formatBtc(getRewardsBtc(log)) }}
                        </p>
                        <span class="slg-row-badge" :style="`background:${getStatusBadgeColor(log.status)}1a;border-color:${getStatusBadgeColor(log.status)}35;color:${getStatusBadgeColor(log.status)};`">
                            {{ formatStatus(log.status) }}
                        </span>
                    </div>

                    <!-- Lock progress strip -->
                    <div v-if="log.unlock_at" class="slg-row-prog">
                        <div class="slg-row-prog-bar">
                            <div class="slg-row-prog-fill"
                                 :style="`width:${calculateLockProgress(log)}%;background:linear-gradient(90deg,#7c3aed,${themeColors.primary});`">
                            </div>
                        </div>
                        <div class="slg-row-prog-meta">
                            <span class="slg-row-prog-txt">{{ isUnlocked(log.unlock_at) ? t('unlocked') : t('lockPeriod') }}</span>
                            <span class="slg-row-prog-pct" :style="`color:${themeColors.primary};`">{{ calculateLockProgress(log) }}%</span>
                        </div>
                    </div>
                    <div v-else class="slg-row-flex-tag">{{ t('flexible') }}</div>
                </div>
            </div>

            <!-- ── DESKTOP TABLE (≥ 1024px) ── -->
            <div v-else class="slg-tbl-wrap">
                <table class="slg-tbl" role="table">
                    <thead>
                        <tr class="slg-tbl-hdr">
                            <th class="slg-th">{{ t('pool') }}</th>
                            <th class="slg-th">{{ t('staked') }}</th>
                            <th class="slg-th">{{ t('current') }}</th>
                            <th class="slg-th">{{ t('rewards') }}</th>
                            <th class="slg-th">{{ t('apy') }}</th>
                            <th class="slg-th">{{ t('lockPeriod') }}</th>
                            <th class="slg-th">{{ t('status') }}</th>
                            <th class="slg-th">{{ t('date') }}</th>
                            <th class="slg-th"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="log in filteredLogs"
                            :key="log.id"
                            class="slg-tr"
                            @click="showStakeDetails(log)"
                        >
                            <!-- Pool -->
                            <td class="slg-td">
                                <div class="slg-td-pool">
                                    <span class="slg-td-dot" :style="`background:${getStatusBadgeColor(log.status)};`"></span>
                                    <div>
                                        <p class="slg-td-pool-name">
                                            {{ log.pool_name }}
                                            <span v-if="log.auto_compound" class="slg-auto-tag">AUTO</span>
                                        </p>
                                        <p class="slg-td-pool-id">#{{ log.stake_id }}</p>
                                    </div>
                                </div>
                            </td>

                            <!-- Staked -->
                            <td class="slg-td">
                                <span class="slg-td-btc">₿{{ formatBtc(getStakeBtc(log)) }}</span>
                            </td>

                            <!-- Current Balance -->
                            <td class="slg-td">
                                <span class="slg-td-btc" :style="`color:${themeColors.primary};`">₿{{ formatBtc(getBalanceBtc(log)) }}</span>
                            </td>

                            <!-- Rewards -->
                            <td class="slg-td">
                                <span class="slg-td-btc" style="color:#10b981;">₿{{ formatBtc(getRewardsBtc(log)) }}</span>
                            </td>

                            <!-- APY -->
                            <td class="slg-td">
                                <span class="slg-td-apy">{{ log.apy_rate }}%</span>
                            </td>

                            <!-- Lock period -->
                            <td class="slg-td">
                                <div v-if="log.unlock_at" class="slg-td-lock">
                                    <div class="slg-td-lock-track">
                                        <div class="slg-td-lock-fill" :style="`width:${calculateLockProgress(log)}%;background:linear-gradient(90deg,#7c3aed,${themeColors.primary});`"></div>
                                    </div>
                                    <span class="slg-td-lock-pct" :style="`color:${isUnlocked(log.unlock_at) ? themeColors.primary : '#e2e8f0'};`">
                                        {{ isUnlocked(log.unlock_at) ? t('unlocked') : calculateLockProgress(log) + '%' }}
                                    </span>
                                </div>
                                <span v-else class="slg-td-flex">{{ t('flexible') }}</span>
                            </td>

                            <!-- Status -->
                            <td class="slg-td">
                                <span class="slg-status-badge" :style="`background:${getStatusBadgeColor(log.status)}1a;border-color:${getStatusBadgeColor(log.status)}35;color:${getStatusBadgeColor(log.status)};`">
                                    <i class="slg-status-dot" :style="`background:${getStatusBadgeColor(log.status)};`"></i>
                                    {{ formatStatus(log.status) }}
                                </span>
                            </td>

                            <!-- Date -->
                            <td class="slg-td">
                                <p class="slg-td-date">{{ formatDate(log.staked_at) }}</p>
                                <p class="slg-td-time">{{ formatTime(log.staked_at) }}</p>
                            </td>

                            <!-- Action -->
                            <td class="slg-td">
                                <button class="slg-view-btn"
                                        :style="`color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.25);background:rgba(${themeColors.primaryRgb},0.08);`"
                                        @click.stop="showStakeDetails(log)">
                                    <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
                                    </svg>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="logs?.data && logs.data.length > 0 && logs.links" class="slg-pagi">
                <span class="slg-pagi-info">
                    {{ t('showingResults', { from: logs.from || 0, to: logs.to || 0, total: logs.total || 0 }) }}
                </span>
                <div class="slg-pagi-btns">
                    <button
                        v-for="(link, i) in logs.links"
                        :key="i"
                        :disabled="!link.url"
                        class="slg-pagi-btn"
                        :class="link.active ? 'slg-pagi-btn--on' : !link.url ? 'slg-pagi-btn--off' : ''"
                        :style="link.active ? `background:${themeColors.primary};color:#0f172a;box-shadow:0 4px 12px rgba(${themeColors.primaryRgb},0.3);` : ''"
                        @click="goToPage(link.url)"
                        v-html="link.label"
                    />
                </div>
            </div>
        </div>

        <!-- Detail Modal -->
        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div v-if="showModal"
                 class="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                 @click.self="closeModal">
                <Transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 scale-95"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-95"
                >
                    <div v-if="showModal"
                         class="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                         role="dialog" aria-modal="true">

                        <!-- Modal header -->
                        <div class="px-6 py-5 border-b border-slate-700/50 sticky top-0 bg-slate-900/98 backdrop-blur-xl z-10">
                            <div class="flex items-center justify-between gap-4">
                                <h3 class="text-lg sm:text-xl font-bold text-white">{{ t('stakeDetails') }}</h3>
                                <button class="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/80 transition-all" @click="closeModal" aria-label="Close">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div v-if="selectedStake" class="p-5 sm:p-6 space-y-4">

                            <!-- Pool identity card -->
                            <div class="rounded-xl p-5 border bg-slate-800/60 border-slate-700/50">
                                <div class="flex items-start justify-between gap-4 mb-4">
                                    <div class="flex items-center gap-3">
                                        <div class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" :style="`background:rgba(${themeColors.primaryRgb},0.15);`">
                                            <svg class="w-5 h-5" :style="`color:${themeColors.primary};`" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 class="text-base font-bold text-white flex items-center gap-2">
                                                {{ selectedStake.pool_name }}
                                                <span v-if="selectedStake.auto_compound" class="slg-auto-tag">AUTO</span>
                                            </h4>
                                            <p class="text-xs text-slate-400">{{ t('stakingPool') }}</p>
                                        </div>
                                    </div>
                                    <span class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-lg border"
                                          :style="`background:${getStatusBadgeColor(selectedStake.status)}20;border-color:${getStatusBadgeColor(selectedStake.status)}40;color:${getStatusBadgeColor(selectedStake.status)};`">
                                        <span class="w-2 h-2 rounded-full" :style="`background:${getStatusBadgeColor(selectedStake.status)};`"></span>
                                        {{ formatStatus(selectedStake.status) }}
                                    </span>
                                </div>
                                <div class="font-mono text-sm font-semibold px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-700/30" :style="`color:${themeColors.primary};`">
                                    ID: {{ selectedStake.stake_id }}
                                </div>
                            </div>

                            <!-- Amounts -->
                            <div class="rounded-xl p-5 border bg-slate-800/50 border-slate-700/50">
                                <h4 class="font-bold text-sm mb-4 text-white flex items-center gap-2">
                                    <span class="w-2 h-2 rounded-full" :style="`background:${themeColors.primary};`"></span>
                                    💰 {{ t('amountDetails') }}
                                </h4>
                                <div class="space-y-3">
                                    <div class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">{{ t('stakedAmount') }}:</span>
                                        <span class="font-bold text-base slg-btc-val" style="color:#FF6F00;">₿{{ formatBtc(getStakeBtc(selectedStake)) }}</span>
                                    </div>
                                    <div v-if="selectedStake.btc_price_usd" class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">BTC Price at Stake:</span>
                                        <span class="font-semibold text-sm text-slate-300">${{ formatNumber(selectedStake.btc_price_usd) }}</span>
                                    </div>
                                    <div class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">{{ t('currentBalance') }}:</span>
                                        <span class="font-bold text-base slg-btc-val" :style="`color:${themeColors.primary};`">₿{{ formatBtc(getBalanceBtc(selectedStake)) }}</span>
                                    </div>
                                    <div class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">{{ t('totalRewards') }}:</span>
                                        <span class="font-bold text-base slg-btc-val" style="color:#10b981;">₿{{ formatBtc(getRewardsBtc(selectedStake)) }}</span>
                                    </div>
                                    <div v-if="!selectedStake.auto_compound && getRewardsBtc(selectedStake) > 0" class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">{{ t('pendingRewards') }}:</span>
                                        <span class="font-bold text-base slg-btc-val" style="color:#a78bfa;">₿{{ formatBtc(getRewardsBtc(selectedStake)) }}</span>
                                    </div>
                                    <div class="flex justify-between items-center pt-3 border-t border-slate-700/50 gap-4">
                                        <span class="font-semibold text-sm text-white">{{ t('apyRate') }}:</span>
                                        <span class="font-bold text-xl text-purple-400">{{ selectedStake.apy_rate }}%</span>
                                    </div>
                                    <div v-if="selectedStake.auto_compound" class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">{{ t('compounding') }}:</span>
                                        <span class="px-3 py-1 text-xs font-semibold rounded bg-purple-400/20 text-purple-400 border border-purple-400/30">{{ t('autoCompound') }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Lock progress -->
                            <div v-if="selectedStake.unlock_at" class="rounded-xl p-5 border bg-slate-800/50 border-slate-700/50">
                                <h4 class="font-bold text-sm mb-4 text-white flex items-center gap-2">
                                    <span class="w-2 h-2 rounded-full" :style="`background:${themeColors.primary};`"></span>
                                    🔒 {{ t('lockProgress') }}
                                </h4>
                                <div class="flex justify-between text-sm mb-3">
                                    <span class="text-slate-300 font-semibold">{{ isUnlocked(selectedStake.unlock_at) ? t('unlocked') : t('lockPeriod') }}</span>
                                    <span class="font-bold" :style="`color:${themeColors.primary};`">{{ calculateLockProgress(selectedStake) }}%</span>
                                </div>
                                <div class="relative w-full bg-slate-700/50 rounded-full h-3 overflow-hidden">
                                    <div class="h-full rounded-full transition-all duration-1000"
                                         :style="`width:${calculateLockProgress(selectedStake)}%;background:linear-gradient(90deg,#7c3aed,${themeColors.primary});`"></div>
                                </div>
                            </div>

                            <!-- Timeline -->
                            <div class="rounded-xl p-5 border bg-slate-800/50 border-slate-700/50">
                                <h4 class="font-bold text-sm mb-4 text-white flex items-center gap-2">
                                    <span class="w-2 h-2 rounded-full" :style="`background:${themeColors.primary};`"></span>
                                    ⏰ {{ t('timeline') }}
                                </h4>
                                <div class="space-y-3">
                                    <div class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">{{ t('staked') }}:</span>
                                        <span class="font-semibold text-sm text-white text-right">{{ formatDateTime(selectedStake.staked_at) }}</span>
                                    </div>
                                    <div v-if="selectedStake.unlock_at" class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">{{ t('unlockDate') }}:</span>
                                        <span class="font-semibold text-sm text-right" :style="`color:${isUnlocked(selectedStake.unlock_at) ? themeColors.primary : '#fff'};`">{{ formatDateTime(selectedStake.unlock_at) }}</span>
                                    </div>
                                    <div v-if="selectedStake.last_claim_at" class="flex justify-between items-center gap-4">
                                        <span class="text-sm text-slate-400">{{ t('lastClaim') }}:</span>
                                        <span class="font-semibold text-sm text-white text-right">{{ formatDateTime(selectedStake.last_claim_at) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div v-if="selectedStake.status === 'active'" class="flex flex-col sm:flex-row gap-3">
                                <button
                                    v-if="isUnlocked(selectedStake.unlock_at)"
                                    @click="handleUnstake(selectedStake)"
                                    class="flex-1 py-3 px-5 rounded-xl font-bold text-sm bg-red-500/15 border border-red-500/30 text-red-400 hover:bg-red-500/25 transition-all"
                                >{{ t('unstake') }}</button>
                                <button
                                    v-if="!selectedStake.auto_compound && getRewardsBtc(selectedStake) > 0"
                                    @click="handleClaimRewards(selectedStake)"
                                    class="flex-1 py-3 px-5 rounded-xl font-bold text-sm bg-purple-400/15 border border-purple-400/30 text-purple-400 hover:bg-purple-400/25 transition-all"
                                >{{ t('claimRewards') }}</button>
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="flex justify-end px-6 py-4 border-t border-slate-700/50 bg-slate-900/60 sticky bottom-0 backdrop-blur-xl">
                            <button class="py-2.5 px-6 rounded-xl font-semibold text-sm bg-slate-800 hover:bg-slate-700 text-white transition-all" @click="closeModal">
                                {{ t('close') }}
                            </button>
                        </div>
                    </div>
                </Transition>
            </div>
        </Transition>
    </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'
import { useToast } from '@/composables/useToast'

const props = defineProps({
    logs: {
        type: [Array, Object],
        default: () => []
    },
    btcPriceUsd: {
        type: Number,
        default: 0
    }
})

const page = usePage()
const { t } = useTranslation()
const { success, error } = useToast()
const activeFilter = ref('all')
const showModal = ref(false)
const selectedStake = ref(null)
const isDesktop = ref(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true)

const handleResize = () => { isDesktop.value = window.innerWidth >= 1024 }

const primaryColor = computed(() => page.props.primaryColor || '#86efac')

const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 134, g: 239, b: 172 }
}
const rgbToHex = (r, g, b) => "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex)
    return rgbToHex(Math.min(255, Math.max(0, rgb.r + amount)), Math.min(255, Math.max(0, rgb.g + amount)), Math.min(255, Math.max(0, rgb.b + amount)))
}

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}))

const currencySymbol = computed(() => page.props.currencySymbol || '$')

const filters = computed(() => [
    { label: t('all'), value: 'all' },
    { label: t('active'), value: 'active' },
    { label: t('completed'), value: 'completed' },
    { label: t('cancelled'), value: 'cancelled' }
])

const logsData = computed(() => {
    if (props.logs && typeof props.logs === 'object' && props.logs.data) return props.logs.data
    return Array.isArray(props.logs) ? props.logs : []
})

const filteredLogs = computed(() => {
    if (activeFilter.value === 'all') return logsData.value
    return logsData.value.filter(log => log.status === activeFilter.value)
})

const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '0.00'
    const number = parseFloat(num)
    if (isNaN(number)) return '0.00'
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number)
}

const formatBtc = (num) => {
    if (num === null || num === undefined || num === '') return '0.000000000000'
    const number = parseFloat(num)
    if (isNaN(number)) return '0.000000000000'
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 12, maximumFractionDigits: 12 }).format(number)
}

// Original staked BTC: computed from the USD stake_amount using the price at stake time.
// btc_amount may have grown (auto-compound) so we prefer the USD/price calculation for "Staked" column.
const getStakeBtc = (log) => {
    const price = (log.btc_price_usd && log.btc_price_usd > 0)
        ? log.btc_price_usd
        : (props.btcPriceUsd > 0 ? props.btcPriceUsd : null)
    if (price) return log.stake_amount / price
    // Last resort: use stored btc_amount
    return parseFloat(log.btc_amount) || 0
}

// Current BTC position: btc_amount is the compounding base and grows with each auto-compound reward.
const getBalanceBtc = (log) => parseFloat(log.btc_amount) || 0

// Accumulated BTC rewards: total_rewards is stored in BTC — no price division needed.
const getRewardsBtc = (log) => parseFloat(log.total_rewards) || 0

const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable')
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return t('invalidDate')
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch { return t('invalidDate') }
}

const formatTime = (dateString) => {
    if (!dateString) return ''
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return ''
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    } catch { return '' }
}

const formatDateTime = (dateString) => {
    if (!dateString) return t('notAvailable')
    try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return t('invalidDate')
        return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch { return t('invalidDate') }
}

const formatStatus = (status) => {
    if (!status || typeof status !== 'string') return t('unknown')
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const calculateLockProgress = (log) => {
    if (!log.unlock_at || !log.staked_at) return 0
    const now = new Date()
    const stakedDate = new Date(log.staked_at)
    const unlockDate = new Date(log.unlock_at)
    const totalDuration = unlockDate - stakedDate
    if (totalDuration <= 0) return 100
    return Math.min(Math.round(((now - stakedDate) / totalDuration) * 100), 100)
}

const isUnlocked = (unlockDate) => {
    if (!unlockDate) return true
    return new Date() >= new Date(unlockDate)
}

const getStatusBadgeColor = (status) => {
    if (!status || typeof status !== 'string') return '#94a3b8'
    switch (status.toLowerCase()) {
        case 'active':    return themeColors.value.primary
        case 'completed': return '#3b82f6'
        case 'cancelled': return '#ef4444'
        default:          return '#94a3b8'
    }
}

const showStakeDetails = (stake) => {
    if (!stake?.id) { error(t('invalidStakeSelected')); return }
    selectedStake.value = stake
    showModal.value = true
}

const closeModal = () => {
    showModal.value = false
    setTimeout(() => { selectedStake.value = null }, 300)
}

const handleUnstake = (stake) => {
    if (!confirm(t('confirmUnstake'))) return
    closeModal()
    router.post(`/user/unstake/${stake.id}`, {}, {
        preserveScroll: true,
        onSuccess: () => success(t('unstakeSuccessful')),
        onError: (errors) => error(errors.error || errors.message || t('unstakeFailed'))
    })
}

const handleClaimRewards = (stake) => {
    closeModal()
    router.post(`/user/claim-rewards/${stake.id}`, {}, {
        preserveScroll: true,
        onSuccess: () => success(t('rewardsClaimedSuccessfully')),
        onError: (errors) => error(errors.error || errors.message || t('claimRewardsFailed'))
    })
}

const goToPage = (url) => {
    if (!url || typeof url !== 'string') return
    router.get(url, {}, {
        preserveState: true,
        preserveScroll: true,
        onError: () => error(t('failedToLoadPage'))
    })
}

const handleKeydown = (event) => {
    if (event.key === 'Escape' && showModal.value) closeModal()
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('resize', handleResize)
})
onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
/* ── Section ── */
.slg { margin-top: 32px; }

/* ── Card ── */
.slg-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(0,0,0,0.45);
    position: relative;
}
.slg-card::before {
    content: '';
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), rgba(232,121,249,0.25), transparent);
    pointer-events: none;
}

/* ── Header ── */
.slg-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-wrap: wrap;
}
.slg-head-l { display: flex; align-items: center; gap: 14px; min-width: 0; }
.slg-head-icon {
    width: 40px; height: 40px; border-radius: 11px; border: 1px solid;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.slg-head-title {
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 800; color: #fff; line-height: 1.2; margin: 0 0 3px;
}
.slg-head-sub { font-size: 11.5px; color: rgba(148,163,184,0.65); margin: 0; }

.slg-total-chip {
    display: flex; align-items: center; gap: 7px;
    padding: 7px 14px; border-radius: 50px; border: 1px solid; flex-shrink: 0;
}
.slg-total-n { font-size: 15px; font-weight: 800; }
.slg-total-l { font-size: 11px; color: rgba(148,163,184,0.6); font-weight: 600; white-space: nowrap; }

/* ── Filters ── */
.slg-filters {
    display: flex; align-items: center; gap: 6px;
    padding: 14px 20px; overflow-x: auto; scrollbar-width: none;
    border-bottom: 1px solid rgba(255,255,255,0.05);
}
.slg-filters::-webkit-scrollbar { display: none; }

.slg-filter-btn {
    padding: 6px 16px; border-radius: 50px; border: 1px solid rgba(255,255,255,0.1);
    background: transparent; color: rgba(148,163,184,0.65); font-size: 12px;
    font-weight: 700; cursor: pointer; white-space: nowrap;
    transition: all 0.2s ease; flex-shrink: 0;
}
.slg-filter-btn:hover:not(.slg-filter-btn--on) {
    color: rgba(255,255,255,0.85); border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.05);
}
.slg-filter-btn--on { border-color: transparent; font-weight: 800; }

/* ── Empty ── */
.slg-empty {
    display: flex; flex-direction: column; align-items: center;
    gap: 10px; padding: 56px 24px; text-align: center;
}
.slg-empty-ic {
    width: 68px; height: 68px; border-radius: 18px; border: 1px solid;
    display: flex; align-items: center; justify-content: center; margin-bottom: 8px;
}
.slg-empty-title { font-size: 15px; font-weight: 700; color: #fff; margin: 0; }
.slg-empty-desc { font-size: 12.5px; color: rgba(148,163,184,0.55); margin: 0; }

/* ── Auto compound tag ── */
.slg-auto-tag {
    display: inline-block;
    font-size: 9px; font-weight: 800; letter-spacing: 0.05em;
    padding: 2px 6px; border-radius: 4px;
    background: rgba(167,139,250,0.18); color: #a78bfa;
    border: 1px solid rgba(167,139,250,0.3);
    vertical-align: middle;
}

/* ── Mobile rows ── */
.slg-mob { display: flex; flex-direction: column; }
.slg-row {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    gap: 6px 12px;
    padding: 13px 18px 13px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    border-left: 3px solid transparent;
    cursor: pointer;
    transition: background 0.18s, border-left-color 0.18s;
    align-items: center;
}
.slg-row:last-child { border-bottom: none; }
.slg-row:hover { background: rgba(167,93,237,0.04); border-left-color: rgba(167,93,237,0.5); }

.slg-row-l {
    display: flex; align-items: center; gap: 10px; min-width: 0;
    grid-column: 1; grid-row: 1;
}
.slg-row-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.slg-row-info { min-width: 0; }
.slg-row-pool {
    font-size: 13px; font-weight: 700; color: #fff; margin: 0 0 2px;
    display: flex; align-items: center; gap: 6px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.slg-row-date { font-size: 11px; color: rgba(148,163,184,0.55); margin: 0; }
.slg-row-btc { font-size: 11px; font-weight: 700; color: #FF6F00; margin: 3px 0 0; font-family: monospace; letter-spacing: -0.01em; }

.slg-row-r {
    display: flex; flex-direction: column; align-items: flex-end; gap: 4px;
    grid-column: 2; grid-row: 1;
}
.slg-row-reward { font-size: 13px; font-weight: 800; margin: 0; line-height: 1; }
.slg-row-badge {
    display: inline-flex; align-items: center; font-size: 10px;
    font-weight: 700; padding: 2px 8px; border-radius: 50px; border: 1px solid;
}

.slg-row-prog { grid-column: 1 / -1; grid-row: 2; }
.slg-row-prog-bar { width: 100%; height: 3px; border-radius: 4px; background: rgba(255,255,255,0.07); overflow: hidden; margin-bottom: 4px; }
.slg-row-prog-fill { height: 100%; border-radius: 4px; transition: width 0.6s ease; }
.slg-row-prog-meta { display: flex; justify-content: space-between; }
.slg-row-prog-txt { font-size: 10px; color: rgba(148,163,184,0.5); }
.slg-row-prog-pct { font-size: 10px; font-weight: 700; }

.slg-row-flex-tag {
    grid-column: 1 / -1; grid-row: 2;
    font-size: 10px; color: rgba(148,163,184,0.45); font-weight: 600;
}

/* ── Desktop table ── */
.slg-tbl-wrap {
    overflow-x: auto; scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent;
}
.slg-tbl-wrap::-webkit-scrollbar { height: 4px; }
.slg-tbl-wrap::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }

.slg-tbl { width: 100%; border-collapse: collapse; min-width: 900px; }
.slg-tbl-hdr { border-bottom: 1px solid rgba(255,255,255,0.06); }
.slg-th {
    padding: 11px 16px; text-align: left; font-size: 10.5px; font-weight: 700;
    letter-spacing: 0.07em; text-transform: uppercase; color: rgba(148,163,184,0.5); white-space: nowrap;
}

.slg-tr { border-bottom: 1px solid rgba(255,255,255,0.04); cursor: pointer; transition: background 0.15s; }
.slg-tr:last-child { border-bottom: none; }
.slg-tr:hover { background: rgba(167,93,237,0.04); }
.slg-td { padding: 13px 16px; vertical-align: middle; }

.slg-td-pool { display: flex; align-items: center; gap: 10px; min-width: 160px; }
.slg-td-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.slg-td-pool-name {
    font-size: 13px; font-weight: 700; color: #fff; margin: 0 0 2px;
    display: flex; align-items: center; gap: 5px;
}
.slg-td-pool-id { font-size: 10.5px; color: rgba(148,163,184,0.45); margin: 0; font-family: monospace; }

.slg-td-val { font-size: 13px; font-weight: 700; color: rgba(226,232,240,0.9); white-space: nowrap; }
.slg-td-btc { font-size: 12px; font-weight: 700; color: #FF6F00; font-family: monospace; white-space: nowrap; letter-spacing: -0.02em; }
.slg-btc-val { font-size: 12px; font-weight: 700; font-family: monospace; letter-spacing: -0.02em; }
.slg-td-apy { font-size: 13px; font-weight: 800; color: #a78bfa; }

.slg-td-lock { display: flex; align-items: center; gap: 8px; min-width: 110px; }
.slg-td-lock-track { flex: 1; height: 5px; border-radius: 5px; background: rgba(255,255,255,0.08); overflow: hidden; }
.slg-td-lock-fill { height: 100%; border-radius: 5px; transition: width 0.7s ease; }
.slg-td-lock-pct { font-size: 11px; font-weight: 700; white-space: nowrap; flex-shrink: 0; }
.slg-td-flex { font-size: 11px; color: rgba(148,163,184,0.5); font-weight: 600; }

.slg-status-badge {
    display: inline-flex; align-items: center; gap: 5px; font-size: 11px;
    font-weight: 700; padding: 4px 10px; border-radius: 50px; border: 1px solid;
    white-space: nowrap;
}
.slg-status-dot {
    width: 6px; height: 6px; border-radius: 50%; display: inline-block;
    animation: slgDotBlink 2s ease-in-out infinite;
}
@keyframes slgDotBlink { 0%,100%{opacity:1} 50%{opacity:0.35} }

.slg-td-date { font-size: 12px; font-weight: 600; color: rgba(226,232,240,0.75); margin: 0 0 2px; white-space: nowrap; }
.slg-td-time { font-size: 10.5px; color: rgba(148,163,184,0.45); margin: 0; }

.slg-view-btn {
    width: 30px; height: 30px; border-radius: 8px; border: 1px solid;
    display: inline-flex; align-items: center; justify-content: center;
    background: transparent; cursor: pointer; transition: all 0.18s;
}
.slg-view-btn:hover { transform: scale(1.1); filter: brightness(1.2); }

/* ── Pagination ── */
.slg-pagi {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; flex-wrap: wrap; padding: 14px 20px;
    border-top: 1px solid rgba(255,255,255,0.06);
}
.slg-pagi-info { font-size: 11.5px; color: rgba(148,163,184,0.55); font-weight: 500; }
.slg-pagi-btns { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.slg-pagi-btn {
    min-width: 32px; height: 32px; padding: 0 10px; border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04);
    color: rgba(148,163,184,0.7); font-size: 12px; font-weight: 700; cursor: pointer;
    transition: all 0.18s; display: inline-flex; align-items: center; justify-content: center;
}
.slg-pagi-btn:hover:not(.slg-pagi-btn--on):not(.slg-pagi-btn--off) {
    background: rgba(255,255,255,0.08); color: #fff; border-color: rgba(255,255,255,0.2);
}
.slg-pagi-btn--on { border-color: transparent; font-weight: 800; color: #0f172a; }
.slg-pagi-btn--off { opacity: 0.3; cursor: not-allowed; }

@media (max-width: 480px) {
    .slg-head { padding: 14px 14px 12px; }
    .slg-total-chip { display: none; }
    .slg-filters { padding: 10px 12px; gap: 5px; }
    .slg-row { padding: 12px 14px; }
    .slg-pagi { padding: 12px 14px; flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>
